import React, { useCallback } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import TextLink from 'LuxKit/TextLink'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useLuxPlusRenewalBonus from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getCanJoinLoyalty from 'loyalty/selectors/getCanJoinLoyalty'
import { getHasPaidLuxPlusMembershipInCurrentRegion } from 'luxPlus/selectors/membership'
import { getIsWithinRenewalGracePeriod } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useLoyaltyEnrolExistingUser, { joinSocieteMessages } from 'loyalty/hooks/useLoyaltyEnrolExistingUser'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusRenewalBonusTimeline, {
  renewalBonusMessages,
  useRenewalRewardAmountText,
  useNoProgressionHeading,
  RenewalTextSkeleton,
  RenewalBonusInnerCard,
  RenewalBonusLadderBox,
  RenewalBonusGiftIcon,
} from '../LandingPage/LuxPlusRenewalBonusTimeline'

const messages = defineMessages({
  subtitle: {
    id: 'QWip1nsfXCX2RpAkJFjn',
    defaultMessage: "Each year you renew {membershipName}, you'll receive bonus Société Points and Status Credits, bringing you closer to your next tier and unlocking more benefits for your next escape.",
    description: 'Subtitle paragraph of the LuxPlus renewal bonus ladder modal',
  },
  // Infinite members earn no Status Credits and have no tier to progress to — points only.
  subtitleInfinite: {
    id: 'MMOplkWcYGgdBC2BaPpt',
    defaultMessage: "Each year you renew {membershipName}, you'll receive bonus Société Points for your next escape.",
    description: 'Subtitle paragraph of the renewal bonus modal for an Infinite member (points only, no Status Credits or tier progression)',
  },
  eyebrowPersonalised: {
    id: 'PGdjQmnPGxXv_Cs2jpsf',
    defaultMessage: "Don't miss your year {year} renewal bonus",
    description: 'Eyebrow above the highlighted next-reward amount for a LuxPlus+ member',
  },
  eyebrowGeneric: {
    id: '_oynn8OYoUf-sT3o7nzc',
    defaultMessage: 'Annual renewal bonus',
    description: 'Eyebrow above the renewal ladder for a member who is not yet a LuxPlus+ member',
  },
  eyebrowNoLadder: {
    id: 'lFOpRGQ4kN6hL23N5VSt',
    defaultMessage: 'Make your next renewal more rewarding',
    description: 'Eyebrow above the next-reward amount when the renewal bonus modal has no ladder to show',
  },
  qualifyHintLuxPlus: {
    id: 'F6Rjap92CAVuPUbutW5Y',
    defaultMessage: '{joinLink} before your next renewal date to qualify.',
    description: 'Hint (with a join-Société link) shown to a LuxPlus+ member who is not a Société member',
  },
  qualifyHintGeneric: {
    id: 'Hqd6tx8maUgNFf7nJuHV',
    defaultMessage: '{joinLink} to qualify.',
    description: 'Hint (with a join-Société link) shown to a member who is not yet a Société member',
  },
  joinLinkLabel: {
    id: 'OgdQqrUD2XxNk5grlzZr',
    defaultMessage: 'Join Société for free',
    description: 'Inline link within the qualify hint to join Société',
  },
  firstBonus: {
    id: 'pZCLzehRRI_mrMlAsDdd',
    defaultMessage: 'Your first bonus',
    description: 'Caption under the current step in the renewal bonus modal ladder',
  },
  becomeMember: {
    id: 'xEZuIalA1i5mgPcxlHv-',
    defaultMessage: 'Become a {membershipName} member',
    description: 'Primary CTA shown to a member who is not a LuxPlus+ member',
  },
})

function LuxPlusRenewalBonusModal() {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const { steps, shouldHideRenewalProgression, isLoading } = useLuxPlusRenewalBonus()
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const isInfinite = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const isWithinRenewalGracePeriod = useAppSelector(getIsWithinRenewalGracePeriod)
  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const canJoinLoyalty = useAppSelector(getCanJoinLoyalty)
  const isLuxPlusMember = useAppSelector(getHasPaidLuxPlusMembershipInCurrentRegion)
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const joinSociete = useLoyaltyEnrolExistingUser()
  const formatRewardAmount = useRenewalRewardAmountText()
  const formatNoProgressionHeading = useNoProgressionHeading()

  // Content mode:
  //  - collapsed:    no ladder (Infinite / no progression) -> single "Receive ..." headline
  //  - personalised: LuxPlus+ members have an active renewal cycle -> highlighted step + amount heading
  //  - generic:      non-LuxPlus+ members -> plain ladder, no personalisation
  const mode = shouldHideRenewalProgression
    ? 'collapsed'
    : isLuxPlusMember
      ? 'personalised'
      : 'generic'

  // Personalised view highlights the 1st renewal (their "first bonus") as the current step.
  const displaySteps = steps.some((step) => step.state === 'current')
    ? steps
    : steps.map((step, index) => (index === 0 ? { ...step, state: 'current' as const } : step))
  const nextReward = displaySteps.find((step) => step.state === 'current') ?? displaySteps[0]
  const renewalYear = nextReward?.year ?? 1

  const eyebrowText = mode === 'collapsed'
    ? intl.formatMessage(messages.eyebrowNoLadder)
    : mode === 'personalised'
      ? intl.formatMessage(messages.eyebrowPersonalised, { year: renewalYear })
      : intl.formatMessage(messages.eyebrowGeneric)

  // The amount heading only shows in collapsed + personalised modes; the generic view has none.
  const headingText = mode === 'collapsed'
    ? formatNoProgressionHeading(nextReward, { hideStatusCredits })
    : mode === 'personalised'
      ? formatRewardAmount(nextReward, { hideStatusCredits })
      : null

  const showLadder = mode !== 'collapsed'
  const ladderSteps = mode === 'personalised' ? displaySteps : steps
  const currentStepCaption = mode === 'personalised'
    ? intl.formatMessage(isWithinRenewalGracePeriod ? renewalBonusMessages.currentStepUnlock : messages.firstBonus)
    : undefined

  // CTA: needs LuxPlus+ first (become a member, blue); otherwise a LuxPlus+ member who isn't a
  // Société member joins Société; a member with both, or with a closed Société account, needs no action.
  const needsLuxPlus = !isLuxPlusMember
  const needsLoyaltyCta = isLuxPlusMember && canJoinLoyalty
  const primaryActionText = needsLuxPlus
    ? intl.formatMessage(messages.becomeMember, { membershipName: LUXURY_PLUS.PROGRAM_NAME })
    : needsLoyaltyCta
      ? intl.formatMessage(joinSocieteMessages.cta)
      : undefined

  const handleJoinSociete = useCallback(() => {
    resolve()
    joinSociete()
  }, [resolve, joinSociete])

  const handlePrimaryAction = useCallback(() => {
    resolve()
    if (needsLuxPlus) {
      handleBecomeMember()
    } else if (needsLoyaltyCta) {
      joinSociete()
    }
  }, [resolve, needsLuxPlus, needsLoyaltyCta, handleBecomeMember, joinSociete])

  // The "Join Société for free … to qualify." line (with inline link) shows to non-Société members.
  // It sits above the ladder in the personalised view and below it in the generic view.
  const showQualifyHint = !isSocieteMember && !isLoading
  const qualifyHint = (
    <BodyTextBlock variant="medium" colour="normal-contrast">
      <FormattedMessage
        {...(isLuxPlusMember ? messages.qualifyHintLuxPlus : messages.qualifyHintGeneric)}
        values={{ joinLink: <TextLink weight="regular" onClick={handleJoinSociete}>{intl.formatMessage(messages.joinLinkLabel)}</TextLink> }}
      />
    </BodyTextBlock>
  )

  const modal = (
    <Modal
      title={intl.formatMessage(renewalBonusMessages.moreYearsMoreRewards)}
      subtitle={intl.formatMessage(isInfinite ? messages.subtitleInfinite : messages.subtitle, { membershipName: LUXURY_PLUS.PROGRAM_NAME })}
      primaryActionText={primaryActionText}
      onPrimaryActionClick={handlePrimaryAction}
    >
      {/* Figma spacing: 12px between the inner card and terms; 20px between the heading, ladder and joining line. */}
      <Group direction="vertical" gap={12}>
        <RenewalBonusInnerCard>
          <Group direction="vertical" gap={20}>
            <Group direction="vertical" gap={4}>
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <RenewalBonusGiftIcon size="XS" />
                {isLoading ? (
                  <RenewalTextSkeleton lineWidths={[220]} height={18} />
                ) : (
                  <BodyTextBlock variant="medium" weight="bold" colour="lux-plus">
                    {eyebrowText}
                  </BodyTextBlock>
                )}
              </Group>
              {isLoading ? (
                <RenewalTextSkeleton lineWidths={[360, 260]} height={28} />
              ) : (
                headingText !== null && (
                  <HeadingTextBlock variant="heading3">
                    {headingText}
                  </HeadingTextBlock>
                )
              )}
              {mode === 'personalised' && showQualifyHint && qualifyHint}
            </Group>
            {(isLoading || showLadder) && (
              <RenewalBonusLadderBox>
                <LuxPlusRenewalBonusTimeline
                  steps={ladderSteps}
                  isLoading={isLoading}
                  currentStepCaption={currentStepCaption}
                  desktopAmountsMaxWidthRem={128}
                />
              </RenewalBonusLadderBox>
            )}
            {mode === 'generic' && showQualifyHint && qualifyHint}
          </Group>
        </RenewalBonusInnerCard>
        <CaptionTextBlock variant="medium" colour="light-foreground-alt">
          {intl.formatMessage(renewalBonusMessages.footnote, { membershipName: LUXURY_PLUS.PROGRAM_NAME })}
        </CaptionTextBlock>
      </Group>
    </Modal>
  )

  // The "Become a LuxPlus+ member" CTA uses the LuxPlus+ blue palette; "Join Société for free"
  // keeps the default palette. Context propagates through the modal portal to the footer button.
  return needsLuxPlus
    ? <ProductPaletteProvider palette="lux-plus">{modal}</ProductPaletteProvider>
    : modal
}

export default LuxPlusRenewalBonusModal
